import React, { FunctionComponent, useState, MouseEvent } from 'react';
import moment from 'moment';
import { Paper, makeStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import { useHistory } from 'react-router-dom';
import { BASE_URL } from '../../../../routes';
import {
    ViolationList,
    Application,
    Order,
    ConfiguredFields,
    IColumn,
} from '../../../../store/modules/application/types';
import Loader from '../../../../components/Loader';
import Modal from '../../../../components/Modal';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import { doubleEncodeURL, getFlatColumnFieldsFromList, getFormattedColumns } from '../../../../utils/utils';
import DeleteDialog from '../../../../components/DeleteDialog';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';
import AddNewViolationDialog from '../../Tabs/AddNewViolationDialog';

type ViolationProps = {
    data: ViolationList;
    deleteViolation: Function;
    loading: boolean;
    skip: number;
    setSkip: Function;
    hasMore: boolean;
    application: Application;
    addViolation: Function;
    inspectionId?: string;
    setSortOrder: Function;
    setSortColumn: Function;
    sortOrder: Order;
    sortBy: string;
    violationColumnFields: ConfiguredFields;
};

const useStyles = makeStyles({
    addViolation: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '25px',
    },
    violationContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

const Violation: FunctionComponent<ViolationProps> = (props) => {
    const [violationId, setViolationId] = useState<string>('');

    /* State for modals */
    const [isDeleteModalOpen, setModal] = useState(false);
    const {
        data,
        loading,
        deleteViolation,
        hasMore,
        skip,
        setSkip,
        application,
        inspectionId,
        addViolation,
        setSortColumn,
        setSortOrder,
        sortOrder,
        sortBy,
        violationColumnFields,
    } = props;

    const classes = useStyles();
    const history = useHistory();
    const [isAddViolationModalOpen, setIsAddViolationModalOpen] = useState(false);
    const columns: IColumn[] = violationColumnFields?.fields?.length
        ? getFormattedColumns(violationColumnFields)
        : [
              {
                  id: 'inspection|string',
                  label: 'Inspection',
                  maxWidth: 300,
                  field: 'inspection.type.name',
                  sortFields: ['inspector.inspectionDate'],
              },
              {
                  id: 'ViolationType|string',
                  label: 'Violation Type',
                  maxWidth: 300,
                  field: 'category.name',
              },
              {
                  id: 'status|string',
                  label: 'Violation Status',
                  align: 'left',
                  maxWidth: 200,
                  field: 'status',
              },
              {
                  id: 'dueDate|date',
                  label: 'Due Date',
                  align: 'left',
                  maxWidth: 300,
                  field: 'dueDate',
                  sortFields: ['dueDate'],
              },
              {
                  id: 'issuedBy|string',
                  label: 'Issued By',
                  align: 'left',
                  maxWidth: 200,
                  field: 'issuedBy.name',
                  sortFields: ['issuedBy.name'],
              },
          ];

    /* Handle delete case button */
    const handleDeleteCase = (id: string, e: any) => {
        setViolationId(id);
        setModal(true);
        e.stopPropagation();
    };
    const onClose = () => {
        setModal(false);
    };
    const onDelete = () => {
        setModal(false);
        deleteViolation(doubleEncodeURL(violationId));
    };
    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/violation/${id}/detail`);
    };
    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    if (loading) {
        return <Loader />;
    }

    const violations = violationColumnFields?.fields?.length
        ? getFlatColumnFieldsFromList(data, columns)
        : data.map((violation: any) => {
              return {
                  id: violation?.id,
                  inspection: violation?.inspection?.type?.name
                      ? violation?.inspection?.type?.name +
                        ' ' +
                        moment(violation?.inspection?.inspectionDate).format('L')!
                      : '',
                  ViolationType: violation?.category?.name,
                  status: violation?.status,
                  issuedBy: violation?.issuedBy?.name,
                  dueDate: violation?.dueDate,
              };
          });

    const handleAddViolationModal = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsAddViolationModalOpen(true);
    };

    const handleAddViolation = (violation: ViolationList) => {
        addViolation(violation);
        setSkip(0);
    };

    return (
        <div className="verticalFillContainer">
            <div className={classes.violationContainer}>
                <Button
                    startIcon={<AddIcon />}
                    className={classes.addViolation}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                        handleAddViolationModal && handleAddViolationModal(e);
                    }}
                >
                    ADD VIOLATION
                </Button>
            </div>
            <Paper className="case-entries-container verticalFillContent">
                {data?.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={violations}
                        count={violations.length}
                        onClickRow={onClickRow}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        deleteFlag={true}
                        handleDeleteCase={handleDeleteCase}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                    />
                ) : (
                    <TableEmptyState
                        title="No active records for this license. "
                        message="All your violations will be stored here"
                    ></TableEmptyState>
                )}
                {isDeleteModalOpen && (
                    <Modal open={true} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                        {isDeleteModalOpen && (
                            <DeleteDialog
                                onClose={onClose}
                                onDelete={onDelete}
                                contentTitle="Are you sure you'd like to proceed with this action?"
                                content=""
                            />
                        )}
                    </Modal>
                )}
            </Paper>
            <Modal open={isAddViolationModalOpen} maxWidth={'md'} fullWidth={true} minHeight={'40vh'}>
                <AddNewViolationDialog
                    eventInspection={inspectionId}
                    setIsAddViolationModalOpen={setIsAddViolationModalOpen}
                    application={application}
                    addViolation={handleAddViolation}
                />
            </Modal>
        </div>
    );
};

export default Violation;
